<template>
    <b-form-select
        v-if="!showInput"
        class="scaleSelector"
        size="sm"
        v-model="selectedScale"
        @input="$_onScaleSelected()"
        :options="filteredScales"
    ></b-form-select>
    <b-input-group size="sm" class="scaleSelector" v-else>
        <b-input-group-prepend>
            <span class="input-group-text">1:</span>
        </b-input-group-prepend>
        <b-form-input
            class="scaleSelector"
            size="sm"
            v-model="customScale"
            type="number"
            @keyup.enter="$_setCustomScale()"
            :placeholder="$t('scaleInput')"
            ref="scaleInput"
        />
    </b-input-group>
</template>
<script>
    import { mapState, mapGetters } from 'vuex';
    import {
        getResolutionFromScale,
        getScaleFromResolution
    } from '../../../services/map';

    export default {
        props: {
            minScale: { type: Number, required: false },
            maxScale: { type: Number, required: false }
        },
        data() {
            return {
                selectedScale: 0,
                customScale: '',
                selectedResolution: 0,
                map: null,
                showInput: false
            };
        },
        computed: {
            ...mapState(['defaultMap']),
            ...mapGetters(['getScales', 'getMapByName']),

            currentResolution() {
                // if we don't have a map get a map.
                if (!this.map) {
                    this.$_getMap();
                }

                // Initially we still may not have a map.
                if (this.map) {
                    return this.map.getView().getResolution();
                } else {
                    return 0;
                }
            },
            scales: function () {
                let scales = [...this.getScales];

                // Add option "overige"
                const otherText = this.$t('labelOther');
                if (!scales.some((s) => s.text === otherText)) {
                    scales.unshift({ text: otherText, value: -1 });
                }
                // Then add "scale" as first choice
                const scaleText = this.$t('printComponent.scaleHeader');
                if (!scales.some((s) => s.text === scaleText)) {
                    scales.unshift({ text: scaleText, value: 0 });
                }
                return scales;
            },
            // remove any scale values from the scale array
            // that are outside the minScale and maxScale if
            // these are specified.
            filteredScales() {
                if (!this.minScale && !this.maxScale) {
                    return this.scales;
                }
                const filteredArray = this.scales.filter((item) => {
                    const value =
                        typeof item.value === 'string'
                            ? Number(item.value)
                            : item.value;

                    // Keep values 0 and -1 always
                    if (value === 0 || value === -1) {
                        return true;
                    }

                    // Handle the open-ended ranges
                    const isGreaterThanMin =
                        this.minScale === null || value >= this.minScale;
                    const isLessThanMax =
                        this.maxScale === null || value <= this.maxScale;

                    return isGreaterThanMin && isLessThanMax;
                });

                return filteredArray;
            }
        },
        methods: {
            // Set the map scale to the scale input parameter
            $_setScale(scale) {
                // if we don't have a map get a map.
                if (!this.map) {
                    this.$_getMap();
                }

                // Initially we still may not have a map.
                if (scale != 0 && this.map) {
                    this.selectedResolution = getResolutionFromScale(
                        this.map,
                        scale
                    );

                    this.map.getView().setResolution(this.selectedResolution);
                }
            },
            // Select list value
            $_onScaleSelected() {
                // If -1 is selected it means we want to show the manual input field
                if (this.selectedScale === -1) {
                    this.customScale = ''; // reset scale values
                    this.selectedScale = 0;
                    this.showInput = true;
                    return;
                }
                // Otherwise set the map scale to the selected value
                this.$_setScale(this.selectedScale);
            },
            // Manual input value
            $_setCustomScale() {
                if (this.customScale > 0) {
                    this.$_setScale(this.customScale);
                }
                // Reset scale selection list
                this.selectedScale = 0;
                // Hide manual input
                this.showInput = false;
            },
            $_getMap() {
                const mapName = this.$store.state.defaultMapName;
                const mapConfig = this.getMapByName(mapName);
                this.map = mapConfig?.map;
            }
        },
        watch: {
            currentResolution(newValue) {
                // If the map has a min or max Scale then prevent
                // the map from zooming beyond these limits.
                if (this.minScale || this.maxScale) {
                    if (!this.map) {
                        this.$_getMap();
                    }
                    const newScale = getScaleFromResolution(this.map, newValue);

                    // Set map scale to minscale if current scale is smaller
                    if (this.minScale && newScale < this.minScale) {
                        this.$_setScale(this.minScale);
                    }

                    // Set map scale to max scale if current scale is larger
                    if (this.maxScale && newScale > this.maxScale) {
                        this.$_setScale(this.maxScale);
                    }
                }

                // If currentResolution is not the selectedResolution reset scale to 'schaal' text.
                // we compare by resolution to avoid rounding errors in the calculates scale value
                if (newValue != this.selectedResolution) {
                    this.selectedScale = 0;
                }
            },
            showInput(newVal) {
                if (newVal) {
                    // Wait for the DOM to update, then focus the input field
                    this.$nextTick(() => {
                        this.$refs.scaleInput.focus();
                    });
                }
            }
        }
    };
</script>

<style scoped>
.input-group-text {
    padding: 0 !important; /* Override the inherited padding */
    margin-right: 0.25rem; /* Optional: add some space between the prefix and input */
    background: transparent;
    border: none;
    color: inherit; /* Match input color */
    font-size: inherit; /* Match input font size */
    line-height: inherit; /* Match input line height */
}

.scaleSelector input {
    width: 130px;
}

/* Hide arrows for Chrome, Safari, Edge, and Opera */
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Hide arrows for Firefox */
input[type='number'] {
    -moz-appearance: textfield;
}
</style>
